// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@conklin/App/Components/Qty/QtyField";
import xUseQty from "@conklin/App/Hooks/xUseQty";
import { MAX_QTY } from "@conklin/XDL/constants";
import { mergeWithCss } from "@conklin/XDL/Utilities";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldProps } from "@insite/mobius/TextField";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: ProductQuantityStyles; // XNG-Change type to be QTY Field
    showLabel?: boolean;
    printLabelOverride?: React.ReactNode;
    // XNG-Change
    isPdp?: boolean;
    onBlurQtyUpdate?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

// XNG-Changes: Styles & Structure
// export interface ProductQuantityOrderedActionsStyles {
//     wrapper?: InjectableCss;
//     actionClickable?: ClickableProps;
//     quantityOrderedTextField?: TextFieldPresentationProps;
//     withQuantityTooltipStyles?: InjectableCss;
// }
// export interface ProductQuantityOrderedStyles extends TextFieldPresentationProps {
//     actionsStyles?: ProductQuantityOrderedActionsStyles;
//     print?: InjectableCss;
//     multipleSaleQtyWrapper?: InjectableCss;
//     questionIconStyles?: IconPresentationProps;
//     multipleSaleQtyToolTip?: TooltipPresentationProps;
// }
export interface ProductQuantityStyles {
    qtyFieldStyles?: QtyFieldStyles;
}

// export const productQuantityOrderedStyles: ProductQuantityOrderedStyles = {
//     sizeVariant: "small",
//     cssOverrides: {
//         formInputWrapper: css`
//             input[type="number"]::-webkit-outer-spin-button,
//             input[type="number"]::-webkit-inner-spin-button {
//                 /* ISC-11023 deal with this. */
//                 /* stylelint-disable-next-line */
//                 -webkit-appearance: none;
//                 margin: 0;
//             }
//         `,
//         inputSelect: css`
//             /* ISC-11023 deal with this. */
//             /* stylelint-disable-next-line */
//             -moz-appearance: textfield;
//             width: auto;
//             text-align: center;
//             @media print {
//                 display: none;
//             }
//         `,
//     },
//     print: {
//         css: css`
//             display: none;
//             @media print {
//                 display: block;
//             }
//         `,
//     },
// };

export const productQuantityStyles: ProductQuantityStyles = {};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        // XNG-Changes: add allowZeroPricing, detail, cantBuy
        product: { id: productId, allowZeroPricing, cantBuy, minimumOrderQty, detail, isVariantParent },
        onQtyOrderedChanged,
        // XNG-Changes: add pricing
        productInfo: { qtyOrdered, failedToLoadInventory, pricing },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    // XNG-Changes
    showLabel,
    isPdp,
    onBlurQtyUpdate = true,
    ...otherProps
}: Props) => {
    // Change styles
    const [styles] = useState(() => mergeWithCss(productQuantityStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    // XNG-Changes
    const [minQtyState, setMinQty] = useState(1);
    // Xng-Changes
    const { debouncedHandleChange, handleChange } = xUseQty({
        minQtyState,
        multipleSaleQty: detail?.multipleSaleQty && Number(detail?.multipleSaleQty),
        onQtyOrderedChanged,
        productId,
        setProductQty,
    });
    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        // Xng-Changes
        // setProductQty(`${qtyOrdered || ""}`);
        debouncedHandleChange(`${Math.max(minimumOrderQty || 1, qtyOrdered) || 1}`);
    }, [qtyOrdered]);

    // XNG-Changes
    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }
    const disabled = isPdp
        ? (!hasEnoughInventory && !failedToLoadInventory) || (isVariantParent && !variantSelectionCompleted)
        : (!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart;

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    // Xng-Changes
    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, 1);
        const qty =
            qtyOrdered === 1 && detail?.multipleSaleQty && detail?.multipleSaleQty > 1
                ? Math.max(minQty, detail?.multipleSaleQty)
                : Math.max(minQty, qtyOrdered);
        debouncedHandleChange(qty.toString());
        setMinQty(minQty);
    }, [minimumOrderQty]);
    // XNG-Changes
    const handleBlur = (event?: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event?.target?.value || productQty;
        if (onBlurQtyUpdate && nextValue) {
            qtyChangeHandler(nextValue, { force: true });
        } else if (Number(productQty) < minQtyState) {
            qtyChangeHandler(`${minQtyState}`, { force: true });
        }
    };
    // XNG-Changes: fix OOTB issue on Quick Order page, send request on blur or on increase or decrease action
    const qtyChangeHandler = (
        event: ChangeEvent<HTMLInputElement> | string,
        { triggerOnQtyChange = false, force = false } = {},
    ) => {
        const value = typeof event === "string" ? event : event?.target.value;

        setProductQty(value);
        // A keystroke or stepper click replaces any queued sync (for example the initial multiple-of qty).
        debouncedHandleChange.cancel();

        if (force) {
            handleChange(value);
        } else if (triggerOnQtyChange) {
            debouncedHandleChange(value);
        }
    };

    // XNG-Changes
    if (cantBuy) {
        return null;
    }

    // XNG-CHANGES: replace textfield by QtyField component
    return (
        <QtyField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="qty"
            min={minimumOrderQty || detail?.multipleSaleQty || 1}
            max={MAX_QTY}
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={(event, options) => qtyChangeHandler(event, { force: !!options?.force })}
            // {...styles}
            // XNG-Changes
            onBlur={handleBlur}
            showLabel={showLabel}
            extendedStyles={styles.qtyFieldStyles}
            disabled={disabled}
            multipleSaleQty={detail?.multipleSaleQty}
        />
    );
};

// XNG-Changes
ProductQuantityOrdered.defaultProps = {
    showLabel: false,
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
